/**
 * React-query hooks over the Nado gateway. Poll cadences are budgeted against
 * Nado's IP rate limit (2400 query-weight/min): an open modal spends ~10
 * weight/min on the selected product's max.
 *
 * Each hook takes the platform's logger — core has no module-scoped one.
 *
 * @see {@link https://docs.nado.xyz/developer-resources/api/rate-limits | Nado rate limits}
 */

import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import type { Hex } from 'viem'
import type { FunLogger } from '../../src/utils/funLogger'
import { NadoGatewayError } from './errors'
import { getNadoFeeRates, getNadoMaxWithdrawable } from './gateway'

const MAX_WITHDRAWABLE_QUERY_KEY = 'nadoMaxWithdrawable'
const MAX_WITHDRAWABLE_REFETCH_MS = 30_000
const FEE_RATES_STALE_MS = 5 * 60_000

/**
 * React-query owns retries (the fetch layer never retries) — up to 2 with
 * backoff, except deterministic api failures, 429s (retrying hammers the
 * limit we just tripped), and timeouts (the caller already waited 30s; the
 * next scheduled poll is the retry).
 */
export function shouldRetryNadoQuery(
  failureCount: number,
  error: unknown,
): boolean {
  if (
    error instanceof NadoGatewayError &&
    (error.kind === 'api' ||
      error.kind === 'timeout' ||
      error.httpStatus === 429)
  ) {
    return false
  }
  return failureCount < 2
}

// skipToken (not `enabled` + casts) — it narrows types AND survives manual
// refetch(), which bypasses `enabled` entirely.
export function useNadoMaxWithdrawable(params: {
  productId: number | undefined
  sender: Hex | undefined
  spotLeverage: boolean
  logger: FunLogger
}) {
  const { productId, sender, spotLeverage, logger } = params
  return useQuery({
    queryKey: [MAX_WITHDRAWABLE_QUERY_KEY, sender, productId, spotLeverage],
    queryFn:
      productId !== undefined && sender
        ? ({ signal }) =>
            getNadoMaxWithdrawable(
              { productId, sender, spotLeverage },
              { logger, signal },
            )
        : skipToken,
    refetchInterval: MAX_WITHDRAWABLE_REFETCH_MS,
    retry: shouldRetryNadoQuery,
    // Toggling borrow re-keys the query, and the empty gap would flash the
    // balance to 0 ("insufficient" past any typed amount), so hold the last
    // answer while the next one loads — unless it could read too high.
    placeholderData: (previous, previousQuery) => {
      const [, previousSender, previousProductId, previousSpotLeverage] =
        previousQuery?.queryKey ?? []
      const isSameAccountAndProduct =
        previousSender === sender && previousProductId === productId
      const borrowTurnedOff = previousSpotLeverage === true && !spotLeverage
      return isSameAccountAndProduct && !borrowTurnedOff ? previous : undefined
    },
  })
}

/**
 * Drops every cached max — prefix match, since each product and borrow state
 * holds its own entry — so a follow-up withdrawal sees the debited balance
 * rather than waiting out the refetch interval.
 */
export function useInvalidateNadoMaxWithdrawable() {
  const queryClient = useQueryClient()
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [MAX_WITHDRAWABLE_QUERY_KEY] })
  }, [queryClient])
}

export function useNadoFeeRates(params: {
  sender: Hex | undefined
  logger: FunLogger
}) {
  const { sender, logger } = params
  return useQuery({
    queryKey: ['nadoFeeRates', sender],
    queryFn: sender
      ? ({ signal }) => getNadoFeeRates(sender, { logger, signal })
      : skipToken,
    staleTime: FEE_RATES_STALE_MS,
    retry: shouldRetryNadoQuery,
  })
}
